"use client";

import {
  ArrowUpRight,
  ChevronRight,
  Mail,
  MapPin,
  PhoneCall,
} from "lucide-react";
import Link from "next/link";
import { BsInstagram, BsYoutube } from "react-icons/bs";
import { FaFacebook } from "react-icons/fa";
import { LiaLinkedin } from "react-icons/lia";

/* ==========================================================
                    PAGE LINKS
========================================================== */

const pageLinks = [
  {
    title: "Home",
    href: "/",
  },
  {
    title: "About Us",
    href: "/about",
  },
  {
    title: "Products",
    href: "/products",
  },
  {
    title: "Blogs",
    href: "/blogs",
  },
];

/* ==========================================================
                    LEGAL LINKS
========================================================== */

const legalLinks = [
  {
    title: "Privacy Policy",
    href: "/privacy",
  },
  {
    title: "Terms & Conditions",
    href: "/terms",
  },
];

/* ==========================================================
                    SOCIAL LINKS
========================================================== */

const socials = [
  {
    icon: FaFacebook,
    href: "#",
    label: "Facebook",
  },
  {
    icon: BsInstagram,
    href: "#",
    label: "Instagram",
  },
  {
    icon: LiaLinkedin,
    href: "#",
    label: "LinkedIn",
  },
  {
    icon: BsYoutube,
    href: "#",
    label: "YouTube",
  },
];

/* ==========================================================
                    COMPONENT
========================================================== */

export default function Footer() {
  return (
    <footer className="relative overflow-hidden bg-[#081C34] text-white">
      {/* ========================================================
                        BACKGROUND
      ======================================================== */}

      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-40 top-0 h-72 w-72 rounded-full bg-[#D4AF37]/10 blur-[100px] sm:h-112.5 sm:w-112.5 sm:blur-[140px]" />

        <div className="absolute -right-40 bottom-0 h-72 w-72 rounded-full bg-white/5 blur-[110px] sm:h-112.5 sm:w-112.5 sm:blur-[140px]" />
      </div>

      {/* ========================================================
                        CONTAINER
      ======================================================== */}

      <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-6">
        {/* ======================================================
                        MAIN FOOTER
        ====================================================== */}

        <section className="grid grid-cols-1 gap-10 border-t border-white/10 py-14 sm:grid-cols-2 sm:gap-12 sm:py-20 lg:grid-cols-[1.6fr_1fr_1fr_1.4fr]">
          {/* ====================================================
                        1. LOGO + SOCIAL
          ==================================================== */}

          <div>
            {/* LOGO */}

            <Link href="/" className="inline-flex items-center gap-3">
              <div>
                <h3 className="font-serif text-2xl font-bold sm:text-3xl">
                  Vanshi Tex
                </h3>

                <p className="mt-1 text-[10px] uppercase tracking-[2px] text-[#D4AF37] sm:text-xs sm:tracking-[3px]">
                  Premium Textile Manufacturer
                </p>
              </div>
            </Link>

            {/* DESCRIPTION */}

            <p className="mt-6 max-w-md text-sm leading-7 text-gray-300 sm:mt-7 sm:text-base sm:leading-8">
              Delivering premium-quality textile manufacturing solutions with
              cutting-edge technology, sustainable production and global export
              capabilities.
            </p>

            {/* SOCIAL LINKS */}

            {/* <div className="mt-8">
              <p className="mb-4 text-xs font-semibold uppercase tracking-[2px] text-[#D4AF37]">
                Follow Us
              </p>

              <div className="flex gap-3">
                {socials.map((item) => {
                  const Icon = item.icon;

                  return (
                    <Link
                      key={item.label}
                      href={item.href}
                      aria-label={item.label}
                      className="group flex h-11 w-11 items-center justify-center rounded-full border border-white/10 text-white transition-all duration-300 hover:border-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A2342] sm:h-12 sm:w-12"
                    >
                      <Icon
                        size={17}
                        className="transition-transform duration-300 group-hover:scale-110 sm:h-4.5 sm:w-4.5"
                      />
                    </Link>
                  );
                })}
              </div>
            </div> */}
          </div>

          {/* ====================================================
                        2. PAGES
          ==================================================== */}

          <div>
            <h3 className="text-lg font-semibold sm:text-xl">Pages</h3>

            <div className="mt-6 space-y-3 sm:mt-7 sm:space-y-4">
              {pageLinks.map((item) => (
                <Link
                  key={item.title}
                  href={item.href}
                  className="group flex items-center gap-2.5 text-sm text-gray-300 transition-all duration-300 hover:text-[#D4AF37] sm:text-base"
                >
                  <ChevronRight
                    size={15}
                    className="shrink-0 transition-transform duration-300 group-hover:translate-x-1"
                  />

                  <span>{item.title}</span>
                </Link>
              ))}
            </div>
          </div>

          {/* ====================================================
                        3. LEGAL
          ==================================================== */}

          <div>
            <h3 className="text-lg font-semibold sm:text-xl">Legal</h3>

            <div className="mt-6 space-y-3 sm:mt-7 sm:space-y-4">
              {legalLinks.map((item) => (
                <Link
                  key={item.title}
                  href={item.href}
                  className="group flex items-center gap-2.5 text-sm text-gray-300 transition-all duration-300 hover:text-[#D4AF37] sm:text-base"
                >
                  <ChevronRight
                    size={15}
                    className="shrink-0 transition-transform duration-300 group-hover:translate-x-1"
                  />

                  <span>{item.title}</span>
                </Link>
              ))}
            </div>
          </div>

          {/* ====================================================
                        4. CONTACT INFO
          ==================================================== */}

          <div>
            <h3 className="text-lg font-semibold sm:text-xl">Contact Info</h3>

            <div className="mt-6 space-y-5 sm:mt-7 sm:space-y-6">
              {/* PHONE */}

              <div className="flex items-start gap-3.5">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/5">
                  <PhoneCall size={17} />
                </div>

                <div>
                  <p className="text-xs uppercase tracking-wider text-gray-500">
                    Phone
                  </p>

                  <p className="mt-1 text-sm leading-6 text-gray-200 sm:text-base">
                    +7 916-591-02-78
                  </p>
                </div>
              </div>

              {/* EMAIL */}

              <div className="flex items-start gap-3.5">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/5">
                  <Mail size={17} />
                </div>

                <div className="min-w-0">
                  <p className="text-xs uppercase tracking-wider text-gray-500">
                    Email
                  </p>

                  <p className="mt-1 break-all text-sm leading-6 text-gray-200 sm:text-base">
                    vanshi-tex@mail.ru
                  </p>
                </div>
              </div>

              {/* ADDRESS */}

              <div className="flex items-start gap-3.5">
                <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/5">
                  <MapPin size={17} />
                </div>

                <div>
                  <p className="text-xs uppercase tracking-wider text-gray-500">
                    Address
                  </p>

                  <p className="mt-1 max-w-xs text-sm leading-6 text-gray-200 sm:text-base sm:leading-7">
                    Russia, 129226, Moscow, m. VDNKH, str.
                    Selskokhozaistveennaya, 4, building 7, shop no.- C6
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ======================================================
                        FOOTER BOTTOM
        ====================================================== */}

        <section className="border-t border-white/10 py-6 sm:py-8 pb-25">
          <div className="flex flex-col items-center justify-between gap-5 sm:gap-6 lg:flex-row">
            {/* COPYRIGHT */}

            <p className="text-center text-xs leading-6 text-gray-400 sm:text-sm sm:leading-7 lg:text-left">
              © {new Date().getFullYear()} Vanshi Tex. All Rights Reserved.
              <span className="mx-2 text-[#D4AF37]">•</span>
              Designed &amp; Developed with ❤️ By{" "}
              <a
                href="https://petabyteinnovations.in"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-300 transition-colors hover:text-[#D4AF37]"
              >
                Petabyte Innovations
              </a>
            </p>

            {/* BACK TO TOP */}

            <button
              type="button"
              onClick={() =>
                window.scrollTo({
                  top: 0,
                  behavior: "smooth",
                })
              }
              className="fixed bottom-5 right-5 group inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/10 bg-[#0A2342] transition-all duration-300 hover:border-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A2342] sm:h-12 sm:w-12"
              aria-label="Back to top"
            >
              <ArrowUpRight
                size={19}
                className="-rotate-45 transition-transform duration-300 group-hover:-translate-y-1 sm:h-5 sm:w-5"
              />
            </button>
          </div>
        </section>
      </div>
    </footer>
  );
}
